<script lang="ts" setup>
import { computed } from 'vue';
import { $fetch } from '@/home/plugins/fetch';
// import PeopleCard from '@/home/components/PeopleCard.vue';
import { useAsyncData } from '@/home/hooks/async-data';

interface CouncilMember {
  id: string | number;
  name: string;
  position: string;
  resume: string;
  photo: string;
  weight: string;
  title?: string;
  intro?: string;
  note?: string;
}

const { data: council } = useAsyncData(
  'about:council',
  () => $fetch('/committee/member/v1/list', {
    data: {
      committeeType: 1,
    },
  }),
);

const chairmen = computed<CouncilMember[]>(() => {
  // 高层组支持多个成员
  return council.value?.filter((item: Record<string, string>) => item.weight == '2') ?? [];
});

const staff = computed(() => {
  return council.value?.filter((item: Record<string, string>) => item.weight == '1') ?? [];
});

const supervisor = computed(() => {
  return council.value?.filter((item: Record<string, string>) => item.weight == '3') ?? [];
});
</script>

<template>
  <section class="page-about-council pt-48px">
    <div class="about-council-container container relative pb-56px">
      <h2 class="m-0 text-center text-32px font-medium <md:text-26">{{ $t('about.council.title') }}</h2>

      <div
        v-for="(member, index) in chairmen"
        :key="member.id"
        class="relative mt-32px min-h-360px flex items-end <md:flex-col <md:items-stretch"
      >
        <!-- 偶数索引: 头像在右侧 -->
        <template v-if="index % 2 === 0">
          <div class="content min-h-300px w-1360px flex flex-col justify-between rounded-4px <2xl:w-1200px <md:w-full">
            <div class="relative w-860px py-36px pl-136px <2xl:w-836px <2xl:px-56px <md:w-full <md:px-24 <md:pb-24 <md:pt-220">
              <div class="flex leading-48px">
                <h4 class="mx-0 my-0 mb-0 text-32px text-white font-semibold">{{ member.name }}</h4>
                <h3 class="m-0 pl-12px text-20px text-white font-medium">{{ member.position }}</h3>
              </div>
              <p class="mx-0 mb-0 mt-20px whitespace-pre-wrap text-justify text-14px text-white font-semibold leading-24px">
                {{ member.resume }}
              </p>
            </div>
          </div>
          <div class="absolute right-144px z-1 h-360px w-240px flex-shrink-0 <2xl:right-64px <md:left-50% <md:right-auto <md:top-24 <md:h-180 <md:w-180 <md:overflow-hidden <md:rounded-full <md:-translate-x-50%">
            <img :data-src="member.photo" :alt="member.name" class="lazyload block h-full w-full object-cover <md:object-contain">
          </div>
        </template>

        <!-- 奇数索引: 头像在左侧 -->
        <template v-else>
          <div class="content content-flipped min-h-300px w-1360px flex flex-col justify-between rounded-4px <2xl:w-1200px <md:w-full">
            <div class="relative w-1224px py-36px pl-500px <2xl:w-1200px <md:w-full <md:px-24 <md:pb-24 <md:pt-220 <2xl:px-56px <2xl:pl-420px">
              <div class="flex leading-48px">
                <h4 class="mx-0 my-0 mb-0 text-32px text-white font-semibold">{{ member.name }}</h4>
                <h3 class="m-0 pl-12px text-20px text-white font-medium">{{ member.position }}</h3>
              </div>
              <p class="mx-0 mb-0 mt-20px whitespace-pre-wrap text-justify text-14px text-white font-semibold leading-24px">
                {{ member.resume }}
              </p>
            </div>
          </div>
          <div class="absolute left-144px z-1 h-360px w-240px flex-shrink-0 <2xl:left-64px <md:left-50% <md:top-24 <md:h-180 <md:w-180 <md:overflow-hidden <md:rounded-full <md:-translate-x-50%">
            <img :data-src="member.photo" :alt="member.name" class="lazyload block h-full w-full object-cover <md:object-contain">
          </div>
        </template>
      </div>

      <ul class="directors mx-0 mb-0 mt-56px flex flex-wrap list-none justify-between p-0" v-if="staff.length">
        <li class="mb-56px h-294px w-652px flex gap-x-34 rounded-4px bg-white px-80px py-24px <2xl:w-572px <md:h-auto <md:w-full <md:flex-col <md:gap-y-16 <md:p-24" v-for="item in staff" :key="item.id">
          <div class="h-full w-176px <md:h-200 <md:w-full">
            <img :data-src="item.photo" :alt="item.name" class="lazyload block h-full w-full object-cover <md:object-contain">
          </div>
          <div class="min-h-0 flex flex-1 flex-col gap-y-8 py-8">
            <div class="flex leading-32">
              <h4 class="m-0 text-24px text-brand-normal font-semibold">{{ item.name }}</h4>
              <h3 class="m-0 pl-8px text-16px text-brand-primary font-medium">{{ item.position }}</h3>
            </div>
            <div class="min-h-0 flex flex-1 flex-col">
              <p class="m-0 text-14px text-black font-medium leading-32px">{{ item.title }}</p>
              <p class="m-0 min-h-0 flex-1 overflow-y-auto text-justify text-14px text-black font-normal leading-24px">
                {{ item.intro }}
              </p>
            </div>
          </div>
        </li>
      </ul>

      <ul class="directors mx-0 mb-0 flex flex-wrap list-none justify-between p-0" v-if="supervisor.length">
        <li class="h-294px w-652px flex gap-x-34 rounded-4px bg-white px-80px py-24px <2xl:w-572px <md:h-auto <md:w-full <md:flex-col <md:gap-y-16 <md:p-24" v-for="item in supervisor" :key="item.title">
          <div class="h-full w-176px <md:h-200 <md:w-full">
            <img :data-src="item.photo" :alt="item.name" class="lazyload block h-full w-full object-cover <md:object-contain">
          </div>
          <div class="in-h-0 flex flex-1 flex-col gap-y-8 py-8">
            <div class="flex leading-32px">
              <h4 class="m0 text-24px text-brand-normal font-semibold">{{ item.name }}</h4>
              <h3 class="m-0 pl-8px text-16px text-brand-primary font-medium">{{ item.position }}</h3>
            </div>
            <div class="min-h-0 flex flex-1 flex-col">
              <p class="m-0 text-14px text-black font-medium leading-32px">{{ item.title }}</p>
              <p class="m-0 h-144px flex-1 overflow-y-auto text-justify text-14px text-black font-normal leading-24px">
                {{ item.intro }}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.page-about-council {
  .content {
    position: relative;

    &::before {
      position: absolute;
      z-index: -1;
      background-image: url(@/home/assets/about/director.svg);
      background-position: center;
      background-size: cover;
      border-radius: inherit;
      content: "";
      inset: 0;
    }

    &.content-flipped::before {
      transform: scaleX(-1);
    }
  }

  .directors {
    li:hover {
      box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.08);
    }
  }
}
</style>
